import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, FileText, Lock, LockOpen, FolderPlus, Folder, CornerLeftUp } from "lucide-react";
import { getToken, authFetch } from "@/adminAuth";

const API = "/api";

const isImage = (name) => /\.(jpe?g|png|gif|webp|svg)$/i.test(name);
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);

export default function AdminMedias() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const [dir, setDir] = useState("tous");
  const [subfolder, setSubfolder] = useState(""); // relatif au dir courant, ex. "team"
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [moveDest, setMoveDest] = useState("");

  useEffect(() => {
    if (!getToken()) navigate("/admin/login");
  }, [navigate]);

  const load = () =>
    authFetch(`${API}/admin/media`)
      .then((r) => r.json())
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]));

  useEffect(() => {
    load();
  }, []);

  if (!items) return <div className="pt-40 min-h-screen px-6 text-[#626773] font-mono text-sm">Chargement…</div>;

  const dirs = ["tous", ...MEDIA_DIRS_FROM(items), "restreint"];
  const inDir = dir === "tous" ? items : items.filter((i) => i.dir === dir);

  // Sous-dossiers du niveau courant
  const prefix = subfolder ? `${subfolder}/` : "";
  const atLevel = inDir.filter((i) => {
    const rel = i.url.replace(/^\/(restreint\/)?[^/]+\//, "");
    return rel.startsWith(prefix);
  });
  const subfolders = [
    ...new Set(
      atLevel
        .map((i) => {
          const rel = i.url.replace(/^\/(restreint\/)?[^/]+\//, "").slice(prefix.length);
          return rel.includes("/") ? rel.split("/")[0] : null;
        })
        .filter(Boolean)
    ),
  ];
  const shown = atLevel.filter((i) => {
    const rel = i.url.replace(/^\/(restreint\/)?[^/]+\//, "").slice(prefix.length);
    return !rel.includes("/") && i.name.toLowerCase().includes(filter.toLowerCase());
  });

  const allShownSelected = shown.length > 0 && shown.every((i) => selected.has(i.url));

  const toggleSelect = (url) => {
    const next = new Set(selected);
    next.has(url) ? next.delete(url) : next.add(url);
    setSelected(next);
  };

  const toggleAll = () => {
    const next = new Set(selected);
    if (allShownSelected) shown.forEach((i) => next.delete(i.url));
    else shown.forEach((i) => next.add(i.url));
    setSelected(next);
  };

  const bulk = async (endpoint, body) => {
    setBusy(true);
    try {
      await authFetch(`${API}/admin/media/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setSelected(new Set());
      await load();
    } finally {
      setBusy(false);
    }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Supprimer définitivement ${selected.size} fichier(s) ?\nLa suppression sera effective sur le site en ligne à la prochaine publication.`)) return;
    setBusy(true);
    try {
      for (const url of selected) {
        await authFetch(`${API}/admin/media?url=${encodeURIComponent(url)}`, { method: "DELETE" });
      }
      setSelected(new Set());
      await load();
    } finally {
      setBusy(false);
    }
  };

  const createFolder = async () => {
    const name = newFolderName.trim().toLowerCase().replace(/[^a-z0-9-_]/g, "-");
    if (!name || dir === "tous" || dir === "restreint") return;
    const dest = subfolder ? `${dir}/${subfolder}/${name}` : `${dir}/${name}`;
    await bulk("mkdir", { dest });
    setNewFolderName("");
    setNewFolderOpen(false);
  };

  const viewRestricted = async (item) => {
    const resp = await authFetch(`${API}/admin/media/file?url=${encodeURIComponent(item.url)}`);
    if (!resp.ok) return;
    const blob = await resp.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const selList = [...selected];
  const selRestricted = selList.filter((u) => u.startsWith("/restreint/"));
  const selPublic = selList.filter((u) => !u.startsWith("/restreint/"));

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="admin-media-page">
      <div className="max-w-6xl mx-auto px-6 sm:px-12">
        <Link to="/admin" data-testid="admin-media-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A0A5B1] hover:text-gold transition-colors">
          <ArrowLeft className="w-4 h-4" /> Les fiches
        </Link>

        <div className="mt-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-serif font-light text-3xl sm:text-4xl">Médiathèque</h1>
            <p className="mt-3 text-sm text-[#626773] max-w-2xl">
              Tout fichier non restreint possède une adresse publique. « Restreindre » retire le fichier du site public (visible ensuite uniquement ici, connecté). Les modifications s'appliquent au site en ligne lors de la prochaine publication.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#A0A5B1] cursor-pointer" data-testid="admin-media-select-all-label">
            <input
              type="checkbox"
              checked={allShownSelected}
              onChange={toggleAll}
              data-testid="admin-media-select-all"
              className="accent-[#C5A059] w-4 h-4"
            />
            Tout sélectionner
          </label>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Rechercher un fichier…"
            data-testid="admin-media-search"
            className="bg-transparent border border-white/15 focus:border-gold outline-none px-4 py-2.5 text-sm text-[#FAF8F5] w-72 transition-colors"
          />
          <div className="flex gap-2 flex-wrap">
            {dirs.map((d) => (
              <button
                key={d}
                onClick={() => { setDir(d); setSubfolder(""); setSelected(new Set()); }}
                data-testid={`admin-media-dir-${d}`}
                className={`text-[11px] uppercase tracking-[0.15em] px-4 py-2 border transition-colors inline-flex items-center gap-1.5 ${
                  dir === d ? "border-gold text-gold" : "border-white/10 text-[#626773] hover:text-gold"
                }`}
              >
                {d === "restreint" && <Lock className="w-3 h-3" />}
                {d}
              </button>
            ))}
          </div>
          {dir !== "tous" && dir !== "restreint" && (
            <button
              onClick={() => setNewFolderOpen(!newFolderOpen)}
              data-testid="admin-media-new-folder"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] px-4 py-2 border border-white/10 text-[#626773] hover:text-gold hover:border-gold/50 transition-colors"
            >
              <FolderPlus className="w-3.5 h-3.5" /> Dossier
            </button>
          )}
          <span className="font-mono text-xs text-[#626773]" data-testid="admin-media-count">{shown.length} fichier{shown.length > 1 ? "s" : ""}</span>
        </div>

        {newFolderOpen && (
          <div className="mt-4 flex items-center gap-3" data-testid="admin-media-new-folder-row">
            <input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={`Nouveau dossier dans ${dir}${subfolder ? "/" + subfolder : ""}…`}
              data-testid="admin-media-new-folder-input"
              className="bg-transparent border border-white/15 focus:border-gold outline-none px-4 py-2 text-sm text-[#FAF8F5] w-80 transition-colors"
              onKeyDown={(e) => e.key === "Enter" && createFolder()}
            />
            <button onClick={createFolder} data-testid="admin-media-create-folder" className="text-[11px] uppercase tracking-[0.15em] px-4 py-2 bg-gold text-ink hover:bg-gold-light transition-colors">
              Créer
            </button>
          </div>
        )}

        {(subfolder || subfolders.length > 0) && dir !== "tous" && (
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="admin-media-folders">
            {subfolder && (
              <button
                onClick={() => setSubfolder(subfolder.split("/").slice(0, -1).join("/"))}
                data-testid="admin-media-up"
                className="inline-flex items-center gap-1.5 text-xs text-[#A0A5B1] hover:text-gold border border-white/10 px-3 py-1.5 transition-colors"
              >
                <CornerLeftUp className="w-3.5 h-3.5" /> ..
              </button>
            )}
            <span className="font-mono text-xs text-gold/70">/{dir}{subfolder ? "/" + subfolder : ""}/</span>
            {subfolders.map((sf) => (
              <button
                key={sf}
                onClick={() => setSubfolder(subfolder ? `${subfolder}/${sf}` : sf)}
                data-testid={`admin-media-folder-${sf}`}
                className="inline-flex items-center gap-1.5 text-xs text-[#A0A5B1] hover:text-gold border border-white/10 hover:border-gold/40 px-3 py-1.5 transition-colors"
              >
                <Folder className="w-3.5 h-3.5" /> {sf}
              </button>
            ))}
          </div>
        )}

        {selected.size > 0 && (
          <div className="mt-6 flex items-center gap-3 flex-wrap glass border border-gold/25 px-5 py-3" data-testid="admin-media-bulkbar">
            <span className="text-xs text-gold font-mono">{selected.size} sélectionné{selected.size > 1 ? "s" : ""}</span>
            {selPublic.length > 0 && (
              <>
                {dir !== "tous" && dir !== "restreint" && (
                  <select
                    value={moveDest}
                    onChange={(e) => {
                      setMoveDest(e.target.value);
                      if (e.target.value) bulk("move", { urls: selPublic, dest: e.target.value });
                    }}
                    data-testid="admin-media-bulk-move"
                    className="bg-ink-card border border-white/15 text-xs text-[#A0A5B1] px-3 py-2 outline-none cursor-pointer"
                  >
                    <option value="">Déplacer vers…</option>
                    <option value={dir}>/{dir}/</option>
                    {subfolders.map((sf) => (
                      <option key={sf} value={subfolder ? `${dir}/${subfolder}/${sf}` : `${dir}/${sf}`}>
                        /{dir}/{subfolder ? subfolder + "/" : ""}{sf}/
                      </option>
                    ))}
                  </select>
                )}
                <button
                  onClick={() => bulk("restrict", { urls: selPublic })}
                  disabled={busy}
                  data-testid="admin-media-bulk-restrict"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] px-4 py-2 border border-gold/40 text-gold hover:bg-gold/10 transition-colors disabled:opacity-50"
                >
                  <Lock className="w-3.5 h-3.5" /> Restreindre l'accès
                </button>
              </>
            )}
            {selRestricted.length > 0 && (
              <button
                onClick={() => bulk("unrestrict", { urls: selRestricted })}
                disabled={busy}
                data-testid="admin-media-bulk-unrestrict"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] px-4 py-2 border border-gold/40 text-gold hover:bg-gold/10 transition-colors disabled:opacity-50"
              >
                <LockOpen className="w-3.5 h-3.5" /> Rendre public
              </button>
            )}
            <button
              onClick={bulkDelete}
              disabled={busy}
              data-testid="admin-media-bulk-delete"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] px-4 py-2 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </button>
            <button onClick={() => setSelected(new Set())} data-testid="admin-media-bulk-clear" className="text-xs text-[#626773] hover:text-[#FAF8F5] transition-colors ml-auto">
              Désélectionner
            </button>
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {shown.map((item) => (
            <div
              key={item.url}
              className={`group relative bg-ink-card border transition-colors ${selected.has(item.url) ? "border-gold" : "border-white/5 hover:border-gold/30"}`}
              data-testid={`admin-media-item-${item.name}`}
            >
              <label className="absolute top-2 left-2 z-10 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.has(item.url)}
                  onChange={() => toggleSelect(item.url)}
                  data-testid={`admin-media-check-${item.name}`}
                  className="accent-[#C5A059] w-4 h-4"
                />
              </label>
              {item.restricted && (
                <span className="absolute top-2 right-2 z-10 text-gold" title="Accès restreint">
                  <Lock className="w-4 h-4" />
                </span>
              )}
              <div className="aspect-[4/3] overflow-hidden bg-ink flex items-center justify-center">
                {isImage(item.name) && !item.restricted ? (
                  <img src={item.url} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <FileText className="w-8 h-8 text-[#626773]" />
                )}
              </div>
              <div className="p-3">
                <p className="text-xs text-[#FAF8F5] truncate" title={item.name}>{item.name}</p>
                <p className="mt-1 text-[10px] font-mono text-[#626773]">{item.dir} · {fmtSize(item.size)}</p>
                <div className="mt-2 flex items-center justify-between">
                  {item.restricted ? (
                    <button onClick={() => viewRestricted(item)} className="text-[10px] uppercase tracking-[0.15em] text-gold/80 hover:text-gold" data-testid={`admin-media-view-${item.name}`}>
                      Voir (admin) ↗
                    </button>
                  ) : (
                    <a href={item.url} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.15em] text-gold/80 hover:text-gold">
                      Voir ↗
                    </a>
                  )}
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Supprimer définitivement « ${item.name} » ?\nEffectif sur le site en ligne à la prochaine publication.`)) return;
                      await authFetch(`${API}/admin/media?url=${encodeURIComponent(item.url)}`, { method: "DELETE" });
                      load();
                    }}
                    data-testid={`admin-media-delete-${item.name}`}
                    className="text-[#626773] hover:text-red-400 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MEDIA_DIRS_FROM(items) {
  return [...new Set(items.filter((i) => !i.restricted).map((i) => i.dir))];
}
